import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Loader } from "semantic-ui-react";
import { get, isEmpty } from "lodash";

import { PromptPreview, ConfirmationModal, PageTitle, PageActionProps } from "@/components";
import { CategoriesServices, CoachService, PromptServices } from "@/services";
import {
  URLS,
  useAccountSettings,
  capitalizeFormatter,
  useComplianceChecker,
  useQueryParam,
  useCategories,
  useSocialCoachSession,
} from "@/utils";
import {
  Media,
  Prompt,
  PromptPreviewObject,
  PromptStatus,
  PromptRequest,
  WebComponents,
  PromptType,
  PromptAccess,
  Category,
} from "@/interfaces";

import { PromptForm } from "./components/PromptForm";
import { modalReducer } from "./welcomeReducer";

import "./styles.scss";
import { validateFeedPromptWithMultimedia, validateMediasRequiremenstByPrompt } from "./validations";
import { EligibilityEditor } from "@/containers";
import PromptStats from "./components/PromptStats";
import { toast } from "sonner";
import { CoachPostDialog } from "@/components";
import { useMemo } from "react";
import { Tabs } from "@/components/ui";
import { EligibilityCriterion } from "@/services/bedrock";

type Props = RouteComponentProps & {
  promptId?: string;
};

const PROMPT_TABS = ["prompt-info", "eligibility", "stats"] as const;

const PromptFC: React.FC<Props> = ({ promptId, history }) => {
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const writePermission = hasWritePermission(WebComponents.PROMPTS);
  const { clearCategoryCache } = useCategories("LIBRARY");

  const [showPostDialog, setShowPostDialog] = React.useState<boolean>(false);
  const [tab, setTab] = useQueryParam("tab", "prompt-info", [...PROMPT_TABS]);

  const [state, dispatch] = React.useReducer(modalReducer, {
    open: false,
    status: "ACTIVE",
    duplicated: false,
    action: "close",
  });

  // Status properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [editMode, setEditMode] = React.useState<boolean>(false);
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);
  const [uploadingContent, setUploadingContent] = React.useState(false);

  // Prompt info properties
  const [prompt, setPrompt] = React.useState<PromptPreviewObject | undefined>(undefined);
  const promptRef = React.useRef<PromptPreviewObject | undefined>(undefined);
  const [originalAccess, setOriginalAccess] = React.useState<PromptAccess | undefined>(undefined);
  const [promptAccess, setPromptAccess] = React.useState<PromptAccess | undefined>(undefined);
  const promptAccessRef = React.useRef<PromptAccess | undefined>(undefined);

  const handlePromptUpdate = React.useCallback((values: any) => {
    setPrompt(values);
    promptRef.current = values;
  }, []);
  const [originalPrompt, setOriginalPrompt] = React.useState<PromptPreviewObject | undefined>(undefined);
  const promptActive: boolean = prompt ? prompt!.status === "ACTIVE" : false;

  // Blocked Words
  const { findBlockedWords } = useComplianceChecker({ coachId });
  const blockedWords: string[] = React.useMemo(() => {
    if (!prompt) {
      return [];
    }

    const content: string[] = [];

    if (prompt.message && prompt.message.length > 0) {
      content.push(prompt.message);
    }

    if (prompt.post?.media) {
      prompt.post.media.forEach(media => {
        media?.transcripts?.forEach(transcript => {
          if (transcript?.content && transcript?.content.length > 0) {
            content.push(transcript.content);
          }
        });
      });
    }

    return findBlockedWords(content);
  }, [prompt, findBlockedWords]);

  const getErrors = (): string[] | undefined => {
    const errors: any[] = [];
    const validMedias = validateMediasRequiremenstByPrompt(prompt);

    if (!prompt?.title) {
      errors.push("Title is required");
    }

    if (blockedWords && blockedWords?.length > 0) {
      errors.push(`Caption or media includes blocked words: ${blockedWords.join(", ")}`);
    }

    if (!validMedias) {
      errors.push("Please edit the files below to meet the required file format and size requirements.");
    }

    if (validateFeedPromptWithMultimedia(prompt)) {
      const someFailed = prompt?.post?.media.some(
        media => !media.validAspectRatio && media.validAspectRatio !== undefined
      );
      if (someFailed) {
        errors.push(
          "One or more of the attached videos/images does not meet the specified file requirements. Please ensure that all attachments meet the requirements in the hover over next to the upload tool below."
        );
      }
    } else {
      prompt?.post?.media.forEach(media => {
        if (media.validAspectRatio == false) {
          const type = media.type.split("/")[0];
          const typeAfterDot = capitalizeFormatter(type);
          errors.push(
            prompt!.type === PromptType.FEED
              ? `This ${type} does not meet our aspect ratio requirements. ${typeAfterDot} must be 1:1, 4:5, 16:9, 1.91:1, 9:16 or 3:4.`
              : "This video does not meet our aspect ratio requirements. Video must be 9:16 or 4:5."
          );
        }

        if (prompt!.type === PromptType.REELS && media.validDimension == false) {
          errors.push(
            "This video does not meet the our minimum resolution requirements. Video must have a minimum resolution of 540 x 960 pixels (1080 x 1920 is recommended)."
          );
        }

        if (media.errors?.wrongBitrate) {
          errors.push("This video does not meet our bitrate requirements. Videos must have maximum bitrate of 25Mbps.");
        }
        if (media.errors?.wrongLength) {
          errors.push(
            "This video does not meet our video duration requirements. Videos must be between 3 and 90 seconds in duration."
          );
        }
        if (media.errors?.wrongSize) {
          const type = media.type.split("/")[0];
          const typeAfterDot = capitalizeFormatter(media.type.split("/")[0]);
          errors.push(`This ${type} does not meet our file size requirements. ${typeAfterDot} must be under 100MB.`);
        }
      });
    }

    return errors.length > 0 ? errors : undefined;
  };

  const loadAccessPreview = async (newCriterion: EligibilityCriterion[] = []): Promise<PromptAccess> => {
    if (promptRef.current?.promptId) {
      return await PromptServices.getAccessPreview(promptRef.current.promptId.toString(), newCriterion);
    } else {
      return await CoachService.getAccessPreview(coachId!, "LIBRARY", newCriterion);
    }
  };

  React.useEffect(() => {
    if (promptId === "new") {
      loadAccessPreview().then(access => {
        setOriginalAccess(access);
        setPromptAccess(access);
        promptAccessRef.current = access;
      });
      setEditMode(false);
    } else if (isFinite(Number(promptId))) {
      setEditMode(true);
      setLoading(true);
      PromptServices.get(Number(promptId))
        .then((response: any) => {
          response = setMediaResourceIds(response);
          handlePromptUpdate(response);
          setOriginalAccess(response.promptAccess);
          setPromptAccess(response.promptAccess);
          promptAccessRef.current = response.promptAccess;
          setOriginalPrompt(response);
        })
        .catch(response => {
          if (response === "This prompt was deleted") {
            toast.info(response);
            history.push(URLS.coach.prompts);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
    }
  }, [promptId]);

  React.useEffect(() => {
    setFormErrorMessages(() => getErrors());
  }, [prompt, blockedWords]);

  /**
   * This is a temporary solution to the sortOrder vs. ID problem.
   * Media items need a sortOrder to compose the post correctly. The
   * ID is a constant, locally unique identifier to allow
   * react-beautiful-dnd Drag and Drop functionality to work correctly.
   *
   * @param fetchedPrompt the prompt we loaded from the database
   */
  const setMediaResourceIds = (fetchedPrompt: Prompt) => {
    let nextId = 0;
    const media = get(fetchedPrompt, "post.media", []);
    const newMedia = media.map((item: Media) => {
      nextId = item.id || nextId + 1;
      return {
        ...item,
        id: nextId,
        validAspectRatio: true,
        validDimension: true,
      };
    });
    return {
      ...fetchedPrompt,
      post: {
        ...fetchedPrompt.post,
        media: newMedia,
      },
    };
  };

  const savePrompt = async (status: PromptStatus): Promise<any> => {
    const currentPrompt = promptRef.current;
    const currentPromptAccess = promptAccessRef.current;
    try {
      // Segmentation validation
      const validMedia = validateMediasRequiremenstByPrompt(currentPrompt);
      const withMessage = get(currentPrompt, "message", "") + get(currentPrompt, "hashtags", "") !== "";
      const withMedia = get(currentPrompt, "post.media.length", 0);
      if (currentPrompt?.title && validMedia && (withMessage || withMedia > 0) && blockedWords?.length === 0) {
        const {
          title,
          message,
          richMessage,
          hashtags,
          allowHashtags,
          allowShortLink,
          smartLinkId,
          type,
          allowComplianceDisclaimer,
        } = currentPrompt;

        setLoading(true);

        const categories: Category[] = currentPrompt?.categories || [];
        const categoryIds: string[] = [];
        let added = false;
        for (const c of categories) {
          if (!c.categoryId || Number(c.categoryId) < 0) {
            added = true;
            const result = await CategoriesServices.add(coachId!, { name: c.name.trim() }, "LIBRARY");
            categoryIds.push(result.categoryId!.toString());
          } else {
            categoryIds.push(c.categoryId.toString());
          }
        }

        if (added) {
          clearCategoryCache();
        }

        const promptToSave: PromptRequest = {
          title,
          message,
          richMessage,
          hashtags,
          deliveryMode: "LIBRARY",
          categoryIds,
          status,
          allowHashtags,
          allowShortLink,
          allowComplianceDisclaimer,
          smartLinkId: allowShortLink ? smartLinkId : undefined,
          type,
          promptAccess: currentPromptAccess,
        };

        setFormErrorMessages(undefined);

        if (!isEmpty(currentPrompt!.media)) {
          promptToSave.mediaMessage = {
            media: currentPrompt!.media!,
            mediaGroupId: currentPrompt!.mediaMessage?.mediaGroupId,
            type: "VIDEO",
          };
        }

        if (currentPrompt!.post && !isEmpty(currentPrompt!.post!.media)) {
          promptToSave.post = currentPrompt!.post;
        }

        let savedPrompt;
        if (currentPrompt?.promptId) {
          promptToSave.promptId = currentPrompt?.promptId;
          savedPrompt = await PromptServices.update(promptToSave, coachId!);
        } else {
          savedPrompt = await PromptServices.create(promptToSave, coachId!);
        }

        const updatedPrompt = { ...currentPrompt, ...savedPrompt };
        handlePromptUpdate(updatedPrompt);
        setOriginalPrompt(updatedPrompt);
        setOriginalAccess(savedPrompt.promptAccess);

        if (savedPrompt?.promptId !== currentPrompt?.promptId) {
          const finalPath = URLS.coach.prompt.replace(":promptId", `${savedPrompt?.promptId}`);
          history.replace(finalPath);
        }
        setLoading(false);
        return true;
      } else {
        setFormErrorMessages(() => getErrors());
        return false;
      }
    } catch (e: any) {
      setFormErrorMessages([e]);
      return false;
    }
  };

  const disablePrompt = async (): Promise<any> => {
    try {
      if (originalPrompt?.title) {
        const categoryIds: string[] | undefined = originalPrompt!.categories!
          ? originalPrompt!.categories!.map(category => {
              return category.categoryId?.toString()!;
            })
          : undefined;

        const promptToSave: PromptRequest = {
          ...originalPrompt,
          title: originalPrompt!.title,
          categoryIds,
          categories: undefined,
          post: undefined,
          status: "DISABLED",
        };

        if (!isEmpty(originalPrompt!.media)) {
          promptToSave.mediaMessage = {
            media: originalPrompt!.media!,
            mediaGroupId: originalPrompt!.mediaMessage?.mediaGroupId,
            type: "VIDEO",
          };
        }

        if (originalPrompt!.post && !isEmpty(originalPrompt!.post!.media)) {
          promptToSave.post = originalPrompt!.post;
        }
        setLoading(true);
        setFormErrorMessages(undefined);

        await PromptServices.update(promptToSave, coachId!)
          .then(() => {
            history.push(URLS.coach.prompts);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setFormErrorMessages(() => getErrors());
      }
    } catch (e: any) {
      setFormErrorMessages([e]);
    }
  };

  const verifyAndSave = async (status: PromptStatus) => {
    if (status === "DISABLED") {
      if (window.confirm("Do you want to remove this prompt?")) {
        await disablePrompt();
      }
    } else {
      const saved = await savePrompt(status);
      if (saved) toast.success("Prompt has been saved!");
    }
  };

  const verifySaveAndPost = async () => {
    await savePrompt("ACTIVE");
    setShowPostDialog(true);
  };

  const closeAndGoToLibrary = () => {
    if (state.action === "cancel") {
      history.push(URLS.coach.prompts);
    } else {
      dispatch({ type: "close" });
    }
  };

  const cancelAction = () => {
    dispatch({
      type: "open_cancel",
      status: promptActive ? "ACTIVE" : "DRAFT",
    });
  };

  const confirmationModal = () => {
    const modalTitle = state.action === "cancel" ? "Do you want to save this prompt?" : "Replace Your Welcome Prompt";
    return (
      <ConfirmationModal
        title={!state.duplicated ? modalTitle : undefined}
        message={""}
        openConfirmationModal={state.open}
        onClose={() => dispatch({ type: "close" })}
        okHandler={async () => {
          dispatch({ type: "close" });
          const saved = await savePrompt(state.status);
          if (saved) toast.success("Prompt has been saved!");
        }}
        rejectHandler={closeAndGoToLibrary}
      />
    );
  };

  const onTabChangeHandler = (newTab: string) => {
    const errors = getErrors();
    if (errors) {
      setFormErrorMessages(errors);
    } else {
      setTab(newTab);
    }
  };

  const promptReady = useMemo(() => {
    const validMedia = validateMediasRequiremenstByPrompt(prompt);
    const withMessage = get(prompt, "message", "") + get(prompt, "hashtags", "") !== "";
    const withMedia = get(prompt, "post.media.length", 0);
    return prompt?.title && validMedia && (withMessage || withMedia > 0) && blockedWords?.length === 0;
  }, [prompt, blockedWords?.length]);

  const primaryAction = useMemo(() => {
    if (writePermission) {
      return {
        text: promptActive ? "Save" : "Save & Publish",
        onClick: () => verifyAndSave("ACTIVE"),
        disabled: loading || uploadingContent || !promptReady,
      };
    }
    return undefined;
  }, [writePermission, uploadingContent, promptReady, loading, promptActive]);

  const secondaryAction = useMemo(() => {
    if (writePermission) {
      return {
        text: "Post / Send",
        onClick: () => verifySaveAndPost(),
        disabled: loading || uploadingContent || !promptReady,
      };
    }
    return undefined;
  }, [writePermission, uploadingContent, promptReady, loading, promptActive]);

  const actions = useMemo(() => {
    const actionList: PageActionProps[] = [];
    if (writePermission) {
      actionList.push({
        text: promptActive ? "Unpublish" : "Save as draft",
        onClick: () => verifyAndSave("DRAFT"),
        disabled: loading || uploadingContent,
      });
      if (prompt?.promptId) {
        actionList.push({
          text: "Delete",
          onClick: () => verifyAndSave("DISABLED"),
          disabled: loading || uploadingContent,
        });
      }
      return actionList;
    }
    return [];
  }, [writePermission, loading, uploadingContent, prompt?.promptId, promptActive]);

  const promptContent = () => {
    if (loading) return <Loader active size="large" />;

    return (
      <Tabs value={tab} onValueChange={onTabChangeHandler}>
        <Tabs.List className="mb-4" variant="menu">
          <Tabs.Trigger value={"prompt-info"}>Prompt Info</Tabs.Trigger>
          <Tabs.Trigger value={"eligibility"}>Eligibility</Tabs.Trigger>
          <Tabs.Trigger value={"stats"}>Performance</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="prompt-info">
          {!loading && (
            <div className="flex flex-row gap-10">
              <div className="grow pb-30">
                <PromptForm
                  disabled={!writePermission}
                  modeEditable={!editMode}
                  prompt={prompt}
                  onContentLoading={setUploadingContent}
                  updatingHandler={handlePromptUpdate}
                  pageErrors={formErrorMessages}
                />
              </div>
              <div className="w-[350px] shrink-0">
                {prompt && (
                  <PromptPreview
                    title={prompt.title}
                    message={prompt.richMessage || prompt.message}
                    hashtags={prompt.hashtags}
                    showAttached={!!prompt.post?.media}
                    showShortLink={prompt.allowShortLink}
                    showComplianceDisclaimer={prompt.allowComplianceDisclaimer}
                    media={get(prompt, "post.media", [])}
                  />
                )}
              </div>
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value={"eligibility"}>
          {originalAccess && promptAccess && (
            <EligibilityEditor
              isNew={!prompt?.promptId}
              originalAccess={originalAccess}
              currentAccess={promptAccess}
              accessType={"LIBRARY"}
              updateHandler={async newCriteria => {
                const updatedAccess = { ...promptAccessRef.current!, eligibilityCriteria: newCriteria };
                setPromptAccess(updatedAccess);
                promptAccessRef.current = updatedAccess;

                const newAccess = await loadAccessPreview(newCriteria);
                setPromptAccess(newAccess);
                promptAccessRef.current = newAccess;
                return newAccess;
              }}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value={"stats"}>
          <PromptStats prompt={prompt} />
        </Tabs.Content>
      </Tabs>
    );
  };

  return (
    <div>
      <PageTitle
        title={editMode ? "Edit Prompt" : "Add a Prompt"}
        actions={actions}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        hideSeparator
        breadcrumbs={[{ name: "Library", link: cancelAction }]}
      />
      {promptContent()}

      {confirmationModal()}
      {showPostDialog && prompt && (
        <CoachPostDialog open={showPostDialog} handleClose={() => setShowPostDialog(false)} prompt={prompt} />
      )}
    </div>
  );
};

export const PromptPage = withRouter(PromptFC);
